import { parseImportDate, parseImportQuantity } from '../import-parsing';

/**
 * Leitura de célula de uma matriz crua de planilha.
 *
 * A matriz chega como `unknown[][]` — o domínio não conhece o leitor de arquivo, que mora
 * em `@obraplan/table-reader` —, e cada célula pode ser texto, número, booleano ou vazia.
 * Estas funções são o único lugar que decide o que cada uma dessas coisas quer dizer, para
 * os dois leitores (GanttPRO e modelo) não discordarem.
 */

export type Matrix = readonly (readonly unknown[])[];

/** O resultado de ler uma célula que pode estar vazia (`null`) ou estar errada (`ok: false`). */
export type Cell<T> = { readonly ok: true; readonly value: T | null } | { readonly ok: false };

/** Cabeçalho comparável: sem acento, sem caixa, espaço colapsado — "Início" casa com "inicio". */
export function normalizeHeader(value: unknown): string {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value)
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Texto aparado e sem espaços repetidos; vazio vira `null`. Número vira o texto dele. */
export function cleanText(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  if (typeof value !== 'string') return null;
  const text = value.replace(/\s+/g, ' ').trim();
  return text === '' ? null : text;
}

export function readNumber(value: unknown): Cell<number> {
  if (typeof value === 'number') return Number.isFinite(value) ? ok(value) : { ok: false };
  const text = cleanText(value);
  if (text === null) return ok(null);
  const parsed = parseImportQuantity(text);
  return parsed.ok && parsed.value !== undefined ? ok(parsed.value) : { ok: false };
}

/**
 * Data da célula → `AAAA-MM-DD`.
 *
 * O serial do Excel chega como número, com a hora na fração (`46204.333…` é 01/07/2026 às
 * 08:00): `parseImportDate` descarta a fração e conta o calendário em UTC, sem fuso (RN-120).
 * Texto em `dd/mm/aaaa` e ISO também passam.
 */
export function readDate(value: unknown): Cell<string> {
  const text = typeof value === 'number' ? String(value) : cleanText(value);
  if (text === null) return ok(null);
  const parsed = parseImportDate(text);
  return parsed.ok && parsed.value !== undefined ? ok(parsed.value) : { ok: false };
}

/** Lista de EDT: `"1.4.2.1.4, 1.4.4.1.1"` → duas. Tolera `;` e o que vier colado depois do código. */
export function readWbsList(value: unknown): string[] {
  const text = cleanText(value);
  if (text === null) return [];
  const codes: string[] = [];
  for (const piece of text.split(/[,;]/)) {
    const code = /^\s*(\d+(?:\.\d+)*)/.exec(piece)?.[1];
    if (code !== undefined && !codes.includes(code)) codes.push(code);
  }
  return codes;
}

/** `true` para "Sim", "S", "1", "x", "verdadeiro"; `false` para o resto, inclusive vazio. */
export function readFlag(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const text = normalizeHeader(value);
  return ['sim', 's', 'x', '1', 'true', 'verdadeiro', 'yes'].includes(text);
}

/** Um código de EDT: números separados por ponto. */
export const WBS_PATTERN = /^\d+(?:\.\d+)*$/;

export interface HeaderMatch {
  /** Posição da linha no vetor (a linha do Excel é esta mais um). */
  readonly rowIndex: number;
  /** Cabeçalho normalizado → índice da coluna (a primeira que aparece). */
  readonly columns: ReadonlyMap<string, number>;
}

/**
 * A primeira linha, dentro do limite, que tem **todos** os cabeçalhos exigidos.
 *
 * Cada exigência é uma lista de grafias aceitas. O cronograma do GanttPRO tem um banner nas
 * quatro primeiras linhas e o cabeçalho na quinta, então "a primeira linha não vazia" — o
 * que o leitor de planilha comum faz — não serve.
 */
export function findHeaderRow(
  matrix: Matrix,
  required: readonly (readonly string[])[],
  scanLimit = 60,
): HeaderMatch | undefined {
  const last = Math.min(matrix.length, scanLimit);
  for (let rowIndex = 0; rowIndex < last; rowIndex += 1) {
    const columns = new Map<string, number>();
    (matrix[rowIndex] ?? []).forEach((cell, index) => {
      const name = normalizeHeader(cell);
      if (name !== '' && !columns.has(name)) columns.set(name, index);
    });
    const complete = required.every((spellings) =>
      spellings.some((spelling) => columns.has(spelling)),
    );
    if (complete) return { rowIndex, columns };
  }
  return undefined;
}

/** A coluna de um cabeçalho, por qualquer das grafias; `undefined` quando nenhuma existe. */
export function columnOf(
  columns: ReadonlyMap<string, number>,
  spellings: readonly string[],
): number | undefined {
  for (const spelling of spellings) {
    const index = columns.get(spelling);
    if (index !== undefined) return index;
  }
  return undefined;
}

function ok<T>(value: T | null): Cell<T> {
  return { ok: true, value };
}
